import moment, { Moment } from 'moment-timezone';
import { v4 as uuidv4 } from 'uuid';
import { SchedulerDetailsTypes } from '../../SchedulerDetails.types';
import { OVERRIDE_TYPE } from '../../overrideType.constants';

// --- Constants ---
/**
 * Minimum buffer time (in minutes) to leave after current time before generating slots.
 * This ensures interviewers have adequate preparation time.
 */
const MINIMUM_BUFFER_TIME_MINUTES = 30;

const convertHoursToMinutes = (hours: number): number => {
  return hours * 60;
};

// --- Interfaces ---
interface Event {
  start: string | Date;
  end: string | Date;
  allDay?: boolean;
  interviewerId?: string | number; // Allow direct ID property
  title?: string;
  subject?: string;
  eventType?: string;
  extendedProps?: {
    interviewerId?: string | number;
    // other props...
  };
}
interface AvailabilitySlot {
  start: string; // Expecting UTC ISO string
  end: string; // Expecting UTC ISO string
}
interface BusyPeriod {
  start: Moment;
  end: Moment;
}
interface BusyPeriodsMap {
  [interviewerId: string | number]: BusyPeriod[];
}

// --- Helper Functions ---

const roundToNearest15Minutes = (momentTime: Moment): Moment => {
  const time = momentTime.clone();
  const minutes = time.minutes();
  const remainder = minutes % 15;
  if (remainder === 0) {
    return time.seconds(0).milliseconds(0);
  }
  return time
    .add(15 - remainder, 'minutes')
    .seconds(0)
    .milliseconds(0);
};

export const createSelfAvailableSlot = (
  dateKey: string,
  start: Moment, // Expected to be in target timezone
  end: Moment, // Expected to be in target timezone
  duration: number,
  isCustomSlot: boolean = false, // Flag to determine if this should use custom styling (for manually created slots)
  overrideType?: number
): SchedulerDetailsTypes.AvailableSlot => {
  const startTimeStr = start.format('HH:mm');
  const endTimeStr = end.format('HH:mm');
  const slotId = `${dateKey}-${startTimeStr}-${endTimeStr}`;

  // Conditional styling based on whether this is a custom slot

  const styling =
    //  isCustomSlot
    //   ? {
    //       title: `Custom Slot (${duration} min)`,
    //       backgroundColor: 'var(--kula-colors-success-100)',
    //       borderColor: 'var(--kula-colors-success-300)',
    //       textColor: 'var(--kula-colors-success-300)',
    //       classNames: ['custom-slot'],
    //       description: `Custom slot created by user (${duration} min)`,
    //     }
    //   :
    {
      title: `Available (${duration} min)`,
      backgroundColor: 'var(--kula-colors-primary-100)',
      borderColor: 'var(--kula-colors-primary-100)',
      textColor: 'var(--kula-colors-primary-700)',
      classNames: undefined,
      description: `Available for self-scheduling (${duration} min)`,
    };

  return {
    id: slotId,
    start: start.toISOString(), // Store as UTC ISO string
    end: end.toISOString(), // Store as UTC ISO string
    category: 'available',
    title: styling.title,
    backgroundColor: styling.backgroundColor,
    borderColor: styling.borderColor,
    textColor: styling.textColor,
    editable: true,
    durationEditable: false,
    startEditable: true,
    classNames: styling.classNames,
    is_custom: isCustomSlot, // Add the is_custom flag directly
    override_type: overrideType ?? OVERRIDE_TYPE.NONE,
    extendedProps: {
      id: uuidv4(),
      priority: 1,
      category: 'available',
      description: styling.description,
      type: 'free-slot',
    },
  };
};

const checkOverlap = (
  busyPeriod: BusyPeriod, // Start/End should be Moment objects in target timezone
  slotStart: Moment, // Moment object in target timezone
  slotEnd: Moment // Moment object in target timezone
): boolean => {
  // Standard exclusive overlap check
  return (
    slotStart.isBefore(busyPeriod.end) && slotEnd.isAfter(busyPeriod.start)
  );
};

const checkInterviewerAvailability = (
  busyPeriods: { start: Moment; end: Moment }[],
  slotStart: Moment,
  slotEnd: Moment
): { isBusy: boolean; nextAvailable?: Moment } => {
  let isBusy = false;
  let nextAvailable: Moment | undefined;

  for (const busyPeriod of busyPeriods) {
    if (checkOverlap(busyPeriod, slotStart, slotEnd)) {
      isBusy = true;
      if (!nextAvailable || busyPeriod.end.isBefore(nextAvailable)) {
        nextAvailable = busyPeriod.end.clone();
      }
      break;
    }
  }

  return { isBusy, nextAvailable };
};

/**
 * Checks slot availability for interviewers.
 * Correctly handles Panel vs 1-on-1 and calculates next check time.
 */
const checkSlotAvailability = ({
  interviewerIds,
  busyPeriodsByInterviewer, // Expects periods clipped to the relevant day, in target timezone
  slotStart, // Moment object in target timezone
  slotEnd, // Moment object in target timezone
  isOneOnOne,
  interviewerCount,
  meetingRoomEventsByDate,
  userMeetingRooms,
  dateKey,
  timezone,
  availabilityByInterviewer, // NEW: Add availability data for each interviewer
}: {
  interviewerIds: Set<string | number>;
  busyPeriodsByInterviewer: BusyPeriodsMap;
  slotStart: Moment;
  slotEnd: Moment;
  isOneOnOne: boolean;
  interviewerCount: number;
  meetingRoomEventsByDate: { [dateKey: string]: Event[] };
  userMeetingRooms: string[];
  dateKey: string;
  timezone: string;
  availabilityByInterviewer: {
    [interviewerId: string | number]: { start: Moment; end: Moment }[];
  }; // NEW: Availability periods for each interviewer
}): { isSlotAvailable: boolean; nextAvailableTime: Moment } => {
  let isSlotAvailable: boolean;
  let nextCheckStartTime = slotEnd.clone(); // Default if slot is available
  let latestConflictEndTime: Moment | null = null;

  // Helper function to check if an interviewer has availability during the slot
  const hasAvailabilityDuringSlot = (
    interviewerId: string | number
  ): boolean => {
    const availabilityPeriods = availabilityByInterviewer[interviewerId] || [];
    return availabilityPeriods.some((period) => {
      // Check if the availability period overlaps with the slot
      return period.start.isBefore(slotEnd) && period.end.isAfter(slotStart);
    });
  };

  if (isOneOnOne && Object.keys(meetingRoomEventsByDate).length > 0) {
    // For one-on-one interviews, we need at least one actual interviewer to be available
    // Example: If we have 3 interviewers and 2 are busy, the slot is still available
    // Meeting rooms are checked separately for busy conflicts only
    let availableInterviewers = 0; // Count actual interviewers with availability
    let earliestNextAvailable: Moment | null = null;
    isSlotAvailable = false;

    // Create a set of meeting room IDs for quick lookup
    const meetingRoomSet = new Set(userMeetingRooms?.map(String));

    interviewerIds.forEach((interviewerId) => {
      // Skip meeting rooms in this loop - they're handled separately below
      if (meetingRoomSet?.has(String(interviewerId))) {
        return;
      }

      const busyPeriods = busyPeriodsByInterviewer[interviewerId];

      // First check if this interviewer has availability during this slot
      const hasAvailability = hasAvailabilityDuringSlot(interviewerId);
      if (!hasAvailability) {
        // If interviewer has no availability, skip them
        return;
      }

      // Check if this interviewer is busy during the slot
      const { isBusy, nextAvailable } = checkInterviewerAvailability(
        busyPeriods,
        slotStart,
        slotEnd
      );

      if (isBusy) {
        // Track the earliest time when any interviewer becomes available again
        if (
          nextAvailable &&
          (!earliestNextAvailable ||
            nextAvailable.isBefore(earliestNextAvailable))
        ) {
          earliestNextAvailable = nextAvailable;
        }
      } else {
        // Interviewer has availability and is not busy
        availableInterviewers++;
      }
    });

    // Slot is available if at least one actual interviewer is free AND has availability
    isSlotAvailable = availableInterviewers > 0;

    // Meeting room events are displayed on the calendar but should NOT block slot generation.
    // Slots should overlap with meeting room events (room availability is informational only).
  } else if (isOneOnOne) {
    // 1-on-1: Available if at least one actual interviewer is free AND has availability.
    // Meeting room events are informational only and should NOT block slot generation.
    let freeInterviewers = 0;

    // Create a set of meeting room IDs for quick lookup
    const meetingRoomSet = new Set(userMeetingRooms.map(String));

    interviewerIds.forEach((id) => {
      const isMeetingRoom = meetingRoomSet.has(String(id));

      if (isMeetingRoom) {
        // Skip meeting rooms - they should not block slot generation
        return;
      } else {
        // For actual interviewers, check both availability and busy time conflicts
        const hasAvailability = hasAvailabilityDuringSlot(id);
        if (!hasAvailability) {
          return; // Skip if no availability
        }

        const busyPeriods = busyPeriodsByInterviewer[id] || [];
        const isBusy = busyPeriods?.some((busy) =>
          checkOverlap(busy, slotStart, slotEnd)
        );
        if (!isBusy) freeInterviewers++;
      }
    });
    // Slot is available if at least one interviewer is free
    isSlotAvailable = freeInterviewers > 0;
    // Note: If !isSlotAvailable, accurately determining the next *possible* slot
    // start for 1-on-1 requires finding the minimum time *at least one* interviewer becomes free,
    // which is more complex. Defaulting to slotEnd and letting the main loop advance works.
  } else {
    // Panel: Available ONLY if ALL actual interviewers are free AND have availability.
    // Meeting room events are informational only and should NOT block slot generation.
    let isAnyInterviewerBusy = false;
    let hasRequiredAvailability = true; // Track if all required interviewers have availability

    // Create a set of meeting room IDs for quick lookup
    const meetingRoomSet = new Set(userMeetingRooms.map(String));

    interviewerIds.forEach((interviewerId) => {
      const isMeetingRoom = meetingRoomSet.has(String(interviewerId));

      if (isMeetingRoom) {
        // Skip meeting rooms - they should not block slot generation
        return;
      } else {
        // For actual interviewers, check both availability and busy time conflicts
        const hasAvailability = hasAvailabilityDuringSlot(interviewerId);
        if (!hasAvailability) {
          hasRequiredAvailability = false;
          return;
        }

        const busyPeriods = busyPeriodsByInterviewer[interviewerId] || [];
        for (const busyPeriod of busyPeriods) {
          // Check if this interviewer has any overlap with the slot
          if (checkOverlap(busyPeriod, slotStart, slotEnd)) {
            isAnyInterviewerBusy = true; // Found a conflict
            // Track the latest end time among *all* conflicts for this slot check
            if (
              !latestConflictEndTime ||
              busyPeriod.end.isAfter(latestConflictEndTime)
            ) {
              latestConflictEndTime = busyPeriod.end.clone();
            }
            // Don't break inner/outer loop, need to check all for latestConflictEndTime
          }
        }
      }
    });

    // Panel slot is available only if:
    // 1. All actual interviewers have availability during the slot
    // 2. No actual interviewer is busy
    isSlotAvailable = hasRequiredAvailability && !isAnyInterviewerBusy;

    if (!isSlotAvailable && latestConflictEndTime) {
      // If unavailable due to conflict, next check must start after the *latest* conflict ends.
      nextCheckStartTime = latestConflictEndTime;
    }
    // If isSlotAvailable is true, nextCheckStartTime remains slotEnd (default).
  }

  return { isSlotAvailable, nextAvailableTime: nextCheckStartTime };
};

const splitMeetingRoomEventsByDate = (
  events: Event[],
  userMeetingRooms: string[],
  dateFormat: string,
  timezone: string
) => {
  let meetingRoomEventsByDate = {};
  events.forEach((event) => {
    if (event?.eventType === 'meeting_room') {
      const startDate = moment(event?.start).tz(timezone).format(dateFormat);
      if (meetingRoomEventsByDate[startDate]) {
        meetingRoomEventsByDate[startDate].push(event);
      } else {
        meetingRoomEventsByDate[startDate] = [event];
      }
    }
  });

  const meetingRoomMap = {};
  userMeetingRooms.forEach((roomId) => {
    meetingRoomMap[roomId] = true;
  });

  return { meetingRoomEventsByDate, meetingRoomMap };
};

/**
 * Main function to calculate available slots for self-scheduling.
 */
export const getSelfAvailableSlots = ({
  commonAvailability, // OLD: For backward compatibility
  individualAvailability, // NEW: Individual availability data by interviewer
  allEvents, // Raw combined events from upstream
  duration,
  selectedDays,
  timezone,
  isOneOnOne = false,
  interviewers,
  userMeetingRooms,
  userCalendarInfo,
  dateFormat,
  minimumNoticeTimeHours, // NEW: Add minimum notice time parameter
  minimumNoticeEnabled,
}: {
  commonAvailability?: AvailabilitySlot[]; // OLD: Made optional for backward compatibility
  individualAvailability?: {
    [interviewerId: string | number]: { start: string; end: string }[];
  }; // NEW: Individual availability by interviewer (optional)
  allEvents: Event[];
  duration: number | undefined | null;
  selectedDays: string[]; // ISO date strings (e.g., "2025-03-03T00:00:00.000Z")
  timezone: string;
  isOneOnOne?: boolean; // If true, generate slots if at least one interviewer is available
  interviewers: (string | number)[];
  userMeetingRooms: string[];
  userCalendarInfo: any;
  dateFormat: { label: string };
  minimumNoticeTimeHours: number;
  minimumNoticeEnabled: boolean;
}): {
  availableSlots: SchedulerDetailsTypes.AvailableSlot[];
  fullDayEventDays: { [dateKey: string]: boolean };
} => {
  const availableSlots: SchedulerDetailsTypes.AvailableSlot[] = [];
  const finalFullDayEventDays: { [dateKey: string]: boolean } = {};
  // *** CRITICAL: Create the Set using the interviewer IDs passed in ***
  // Ensure the calling code passes the *correct* list with matching types (e.g., strings)
  const interviewerIds = new Set<string | number>(interviewers);
  const interviewerCount = interviewerIds.size;
  const slotDurationMinutes = duration || 30;
  const localDateFormat = dateFormat?.label || 'DD/MM/YYYY';
  const { meetingRoomEventsByDate } = splitMeetingRoomEventsByDate(
    allEvents,
    userMeetingRooms,
    localDateFormat,
    timezone
  );

  // --- Input Validations ---
  if (!duration || duration <= 0) {
    return { availableSlots: [], fullDayEventDays: {} };
  }
  if (interviewerCount === 0) {
    return { availableSlots: [], fullDayEventDays: {} };
  }
  if (!timezone || !moment.tz.zone(timezone)) {
    return { availableSlots: [], fullDayEventDays: {} };
  }
  if (!localDateFormat) {
    return { availableSlots: [], fullDayEventDays: {} };
  }

  const nowLocal: Moment = moment().tz(timezone);

  const minimumNoticeTimeMinutes = minimumNoticeEnabled
    ? convertHoursToMinutes(minimumNoticeTimeHours) + 5
    : 0;
  const minimumNoticeEndTime = nowLocal
    .clone()
    .add(minimumNoticeTimeMinutes, 'minutes');

  // Build availability data by interviewer from individual availability OR commonAvailability
  const availabilityByInterviewer: {
    [interviewerId: string | number]: { start: Moment; end: Moment }[];
  } = {};

  if (individualAvailability) {
    // Use individual availability data
    interviewerIds.forEach((interviewerId) => {
      availabilityByInterviewer[interviewerId] = [];

      // Get individual availability from the passed data
      const interviewerAvailability =
        individualAvailability[interviewerId] || [];

      interviewerAvailability.forEach((slot) => {
        const startMoment = moment.utc(slot.start).tz(timezone);
        const endMoment = moment.utc(slot.end).tz(timezone);

        if (
          startMoment.isValid() &&
          endMoment.isValid() &&
          endMoment.isAfter(startMoment)
        ) {
          availabilityByInterviewer[interviewerId].push({
            start: startMoment,
            end: endMoment,
          });
        }
      });
    });
  } else if (commonAvailability) {
    // OLD APPROACH: Use commonAvailability (backward compatibility)

    // For backward compatibility, assign the same commonAvailability to all interviewers
    interviewerIds.forEach((interviewerId) => {
      availabilityByInterviewer[interviewerId] = [];

      commonAvailability.forEach((slot) => {
        const startMoment = moment.utc(slot.start).tz(timezone);
        const endMoment = moment.utc(slot.end).tz(timezone);

        if (
          startMoment.isValid() &&
          endMoment.isValid() &&
          endMoment.isAfter(startMoment)
        ) {
          availabilityByInterviewer[interviewerId].push({
            start: startMoment,
            end: endMoment,
          });
        }
      });
    });
  } else {
    // Neither parameter provided
    throw new Error(
      'getSelfAvailableSlots: Either individualAvailability or commonAvailability must be provided'
    );
  }

  // NEW: Create commonAvailability for day-level processing (union for one-on-one, intersection for panel)
  const processedCommonAvailability: AvailabilitySlot[] = [];

  if (isOneOnOne) {
    // For one-on-one, create union of all availability
    const allSlots: { start: Moment; end: Moment }[] = [];
    Object.values(availabilityByInterviewer).forEach((slots) => {
      allSlots.push(...slots);
    });

    // Sort and merge overlapping slots
    allSlots.sort((a, b) => a.start.valueOf() - b.start.valueOf());
    let mergedSlots: { start: Moment; end: Moment }[] = [];

    if (allSlots.length > 0) {
      let current = {
        start: allSlots[0].start.clone(),
        end: allSlots[0].end.clone(),
      };

      for (let i = 1; i < allSlots.length; i++) {
        const next = allSlots[i];
        if (next.start.isSameOrBefore(current.end)) {
          // Overlapping or adjacent, merge
          if (next.end.isAfter(current.end)) {
            current.end = next.end.clone();
          }
        } else {
          // No overlap, add current and start new
          mergedSlots.push(current);
          current = { start: next.start.clone(), end: next.end.clone() };
        }
      }
      mergedSlots.push(current);
    }

    // Convert to AvailabilitySlot format
    mergedSlots.forEach((slot) => {
      processedCommonAvailability.push({
        start: slot.start.toISOString(),
        end: slot.end.toISOString(),
      });
    });
  } else {
    // For panel, create intersection of all availability
    // All interviewers must be available during the same time periods
    // IMPORTANT: Exclude meeting rooms from intersection calculation - they don't have working hours
    const meetingRoomSet = new Set(userMeetingRooms.map(String));
    const actualInterviewerIds = Array.from(interviewerIds).filter(
      (id) => !meetingRoomSet.has(String(id))
    );

    if (actualInterviewerIds.length === 0) {
      // No actual interviewers, no availability
      return { availableSlots: [], fullDayEventDays: {} };
    }

    // Get all interviewer availability arrays (excluding meeting rooms)
    const availabilityArrays = actualInterviewerIds.map(
      (interviewerId) => availabilityByInterviewer[interviewerId] || []
    );

    // If any actual interviewer has no availability, there's no intersection
    if (availabilityArrays.some((slots) => slots.length === 0)) {
      return { availableSlots: [], fullDayEventDays: {} };
    }

    // Start with the first interviewer's availability
    let intersectionSlots: { start: Moment; end: Moment }[] = [
      ...availabilityArrays[0],
    ];

    // For each subsequent interviewer, find intersection with current result
    for (let i = 1; i < availabilityArrays.length; i++) {
      const currentInterviewerSlots = availabilityArrays[i];
      const newIntersection: { start: Moment; end: Moment }[] = [];

      // Check each slot in current intersection against each slot of current interviewer
      intersectionSlots.forEach((intersectionSlot) => {
        currentInterviewerSlots.forEach((interviewerSlot) => {
          // Find overlap between these two slots
          const overlapStart = moment.max(
            intersectionSlot.start,
            interviewerSlot.start
          );
          const overlapEnd = moment.min(
            intersectionSlot.end,
            interviewerSlot.end
          );

          // If there's a valid overlap, add it to the new intersection
          if (overlapEnd.isAfter(overlapStart)) {
            newIntersection.push({
              start: overlapStart.clone(),
              end: overlapEnd.clone(),
            });
          }
        });
      });

      intersectionSlots = newIntersection;

      // If at any point the intersection becomes empty, we can stop early
      if (intersectionSlots.length === 0) {
        break;
      }
    }

    // Sort and merge overlapping intersection slots
    if (intersectionSlots.length > 0) {
      intersectionSlots.sort((a, b) => a.start.valueOf() - b.start.valueOf());
      let mergedSlots: { start: Moment; end: Moment }[] = [];

      let current = {
        start: intersectionSlots[0].start.clone(),
        end: intersectionSlots[0].end.clone(),
      };

      for (let i = 1; i < intersectionSlots.length; i++) {
        const next = intersectionSlots[i];
        if (next.start.isSameOrBefore(current.end)) {
          // Overlapping or adjacent, merge
          if (next.end.isAfter(current.end)) {
            current.end = next.end.clone();
          }
        } else {
          // No overlap, add current and start new
          mergedSlots.push(current);
          current = { start: next.start.clone(), end: next.end.clone() };
        }
      }
      mergedSlots.push(current);

      // Convert to AvailabilitySlot format
      mergedSlots.forEach((slot) => {
        processedCommonAvailability.push({
          start: slot.start.toISOString(),
          end: slot.end.toISOString(),
        });
      });
    }
  }

  // --- Pre-process allEvents into Moments & identify full-day blocks ---
  const processedTimedEvents: {
    start: Moment;
    end: Moment;
    interviewerId: string | number;
  }[] = [];
  const fullDayEventBlocks: { [dateKey: string]: Set<string | number> } = {};

  allEvents.forEach((event) => {
    // Use direct or extendedProps ID, ensure it's in the set we care about
    const eventInterviewerId =
      event.interviewerId ?? event.extendedProps?.interviewerId;
    if (
      !event.start ||
      !event.end ||
      !eventInterviewerId ||
      !interviewerIds.has(eventInterviewerId)
    ) {
      return; // Skip if missing info or not a selected interviewer
    }

    // Use moment.utc() to handle 'Z' correctly before converting to local timezone
    const startMomentUTC = moment.utc(event.start);
    const endMomentUTC = moment.utc(event.end);

    if (
      !startMomentUTC.isValid() ||
      !endMomentUTC.isValid() ||
      !endMomentUTC.isAfter(startMomentUTC)
    ) {
      return; // Skip invalid events
    }

    const startMomentLocal = startMomentUTC.clone().tz(timezone);
    const endMomentLocal = endMomentUTC.clone().tz(timezone);

    // Check for all-day: Explicit flag OR spans >=24h AND aligns with local midnight
    const isAllDay =
      event.allDay ||
      (endMomentLocal.diff(startMomentLocal, 'hours') >= 24 &&
        startMomentLocal.isSame(startMomentLocal.clone().startOf('day')) &&
        endMomentLocal.isSame(endMomentLocal.clone().startOf('day')));

    if (isAllDay) {
      let currentDay = startMomentLocal.clone().startOf('day');
      let lastDay = endMomentLocal.clone().startOf('day');
      // Adjust last day if event ends exactly at midnight (exclusive end)
      if (endMomentLocal.isSame(lastDay)) {
        lastDay.subtract(1, 'day');
      }
      // Add interviewer to the block set for each fully covered day
      while (currentDay.isSameOrBefore(lastDay, 'day')) {
        const dateKey = currentDay.format(localDateFormat);
        if (!fullDayEventBlocks[dateKey])
          fullDayEventBlocks[dateKey] = new Set();
        fullDayEventBlocks[dateKey].add(eventInterviewerId);
        currentDay.add(1, 'day');
      }
    } else {
      // Add timed event (with local Moment objects) to list for daily processing
      processedTimedEvents.push({
        start: startMomentLocal,
        end: endMomentLocal,
        interviewerId: eventInterviewerId,
      });
    }
  });

  // Determine final list of fully blocked days
  Object.entries(fullDayEventBlocks).forEach(
    ([dateKey, blockedInterviewers]) => {
      if (
        (isOneOnOne && blockedInterviewers.size === interviewerCount) ||
        (!isOneOnOne && blockedInterviewers.size > 0)
      ) {
        finalFullDayEventDays[dateKey] = true;
      }
    }
  );

  // --- Iterate through selected days and generate slots ---
  selectedDays.sort();
  selectedDays.forEach((dayISOString: string) => {
    const selectedDayMomentLocal = moment
      .utc(dayISOString)
      .tz(timezone)
      .startOf('day');
    const dateKey = selectedDayMomentLocal.format(localDateFormat);

    if (
      finalFullDayEventDays[dateKey] ||
      selectedDayMomentLocal.isBefore(nowLocal.clone().startOf('day'), 'day')
    ) {
      return; // Skip blocked or past days
    }

    // Find and clip availability windows for this day using processedCommonAvailability
    let dayAvailabilitySlots: { start: Moment; end: Moment }[] = [];
    const dayStartBoundary = selectedDayMomentLocal.clone();
    const dayEndBoundary = selectedDayMomentLocal.clone().endOf('day');

    processedCommonAvailability.forEach((availability) => {
      const availStartLocal = moment.utc(availability.start).tz(timezone);
      const availEndLocal = moment.utc(availability.end).tz(timezone);
      if (
        availStartLocal.isBefore(dayEndBoundary) &&
        availEndLocal.isAfter(dayStartBoundary)
      ) {
        const effectiveStart = moment.max(availStartLocal, dayStartBoundary);
        const effectiveEnd = moment.min(availEndLocal, dayEndBoundary);
        if (effectiveEnd.isAfter(effectiveStart))
          dayAvailabilitySlots.push({
            start: effectiveStart,
            end: effectiveEnd,
          });
      }
    });

    if (dayAvailabilitySlots.length === 0) return; // Skip day if no relevant availability
    dayAvailabilitySlots.sort((a, b) => a.start.valueOf() - b.start.valueOf());

    // Prepare busy periods map specific to this day
    const busyPeriodsForThisDay: BusyPeriodsMap = {};
    interviewerIds.forEach((id) => {
      busyPeriodsForThisDay[id] = [];
    });
    processedTimedEvents.forEach((event) => {
      if (!interviewerIds.has(event.interviewerId)) return;
      if (
        event.start.isBefore(dayEndBoundary) &&
        event.end.isAfter(dayStartBoundary)
      ) {
        const busyStart = moment.max(event.start, dayStartBoundary);
        const busyEnd = moment.min(event.end, dayEndBoundary);
        if (busyEnd.isAfter(busyStart))
          busyPeriodsForThisDay[event.interviewerId].push({
            start: busyStart,
            end: busyEnd,
          });
      }
    });

    // Sort and Merge Busy Periods
    Object.keys(busyPeriodsForThisDay).forEach((interviewerId) => {
      // ... (Sorting/Merging logic as before) ...
      const periods = busyPeriodsForThisDay[interviewerId];
      if (periods.length <= 1) return;
      periods.sort((a, b) => a.start.valueOf() - b.start.valueOf());
      const mergedPeriods: BusyPeriod[] = [];
      let currentMerged = { ...periods[0] };
      for (let i = 1; i < periods.length; i++) {
        const nextPeriod = periods[i];
        if (nextPeriod.start.isSameOrBefore(currentMerged.end)) {
          if (nextPeriod.end.isAfter(currentMerged.end))
            currentMerged.end = nextPeriod.end.clone();
        } else {
          mergedPeriods.push(currentMerged);
          currentMerged = { ...nextPeriod };
        }
      }
      mergedPeriods.push(currentMerged);
      busyPeriodsForThisDay[interviewerId] = mergedPeriods;
    });

    // Generate slots within each availability window for the day
    dayAvailabilitySlots.forEach((window) => {
      let windowStart = window.start.clone();
      const windowEnd = window.end.clone();

      // If this is today, skip windows that have already ended
      if (selectedDayMomentLocal.isSame(nowLocal, 'day')) {
        if (windowEnd.isBefore(nowLocal)) {
          // Skip this window - it's already over
          return;
        }
        if (windowEnd.isBefore(minimumNoticeEndTime)) {
          return;
        }

        if (windowStart.isBefore(nowLocal)) {
          // Add buffer time after current time
          const currentTimeWithBuffer = nowLocal
            .clone()
            .add(MINIMUM_BUFFER_TIME_MINUTES, 'minutes');
          windowStart = currentTimeWithBuffer;

          // Skip if the window would end before we can fit even one slot after the buffer
          if (
            windowEnd.isSameOrBefore(
              windowStart.clone().add(slotDurationMinutes, 'minutes')
            )
          ) {
            // Window is too short after applying buffer time
            return;
          }
        }
        if (windowStart.isBefore(minimumNoticeEndTime)) {
          windowStart = minimumNoticeEndTime.clone();
          if (
            windowEnd.isSameOrBefore(
              windowStart.clone().add(slotDurationMinutes, 'minutes')
            )
          ) {
            return;
          }
        }
      } else {
        const dayEnd = selectedDayMomentLocal.clone().endOf('day');
        if (dayEnd.isBefore(minimumNoticeEndTime)) {
          return;
        }
        if (windowStart.isBefore(minimumNoticeEndTime)) {
          windowStart = moment.max(windowStart, minimumNoticeEndTime);
          if (
            windowEnd.isSameOrBefore(
              windowStart.clone().add(slotDurationMinutes, 'minutes')
            )
          ) {
            return;
          }
        }
      }

      let currentTime = roundToNearest15Minutes(windowStart);
      let loopGuard = 0;
      const maxLoops = 500; // Safety break

      while (currentTime.isBefore(windowEnd) && loopGuard < maxLoops) {
        loopGuard++;
        const potentialSlotEnd = currentTime
          .clone()
          .add(slotDurationMinutes, 'minutes');
        if (potentialSlotEnd.isAfter(windowEnd)) break;

        const { isSlotAvailable, nextAvailableTime } = checkSlotAvailability({
          interviewerIds,
          busyPeriodsByInterviewer: busyPeriodsForThisDay,
          slotStart: currentTime,
          slotEnd: potentialSlotEnd,
          isOneOnOne,
          interviewerCount,
          meetingRoomEventsByDate,
          userMeetingRooms,
          dateKey,
          timezone,
          availabilityByInterviewer,
        });

        if (isSlotAvailable) {
          const newSlot = createSelfAvailableSlot(
            dateKey,
            currentTime,
            potentialSlotEnd,
            slotDurationMinutes,
            false // This is a generated slot from checkbox, use primary styling
          );
          availableSlots.push(newSlot);
          currentTime = potentialSlotEnd.clone();
        } else {
          if (nextAvailableTime.isSameOrBefore(currentTime)) {
            // Prevent infinite loop if nextAvailableTime doesn't advance
            console.warn(
              `[${dateKey}] Infinite loop guard triggered. Conflict end ${nextAvailableTime.format()}, current ${currentTime.format()}. Forcing advance.`
            );
            currentTime = roundToNearest15Minutes(
              currentTime.clone().add(slotDurationMinutes, 'minutes')
            );
          } else {
            currentTime = roundToNearest15Minutes(nextAvailableTime);
          }
        }
      }
      if (loopGuard >= maxLoops)
        console.error(
          `[${dateKey}] Loop guard exceeded for availability window.`
        );
    }); // End forEach availabilityWindow
  }); // End forEach selectedDays

  const uniqueSlotIds = new Set<string>();
  const deDuplicatedAvailableSlots = availableSlots.filter((slot) => {
    if (!slot || typeof slot.id !== 'string') {
      return false;
    }
    if (uniqueSlotIds.has(slot.id)) {
      return false; // Exclude duplicate
    } else {
      uniqueSlotIds.add(slot.id);
      return true; // Include unique
    }
  });

  return {
    availableSlots: deDuplicatedAvailableSlots,
    fullDayEventDays: finalFullDayEventDays,
  };
};
